import useCustomHook from '@/hooks/useCustomHook';
import openAPIErrorNotification from '@/shared/APIErrorNotification/APIErrorNotification';
import { createContext, useContext, useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';
import { v4 as uuidv4 } from 'uuid';
import { StudentContext } from '@/shared/StudentContext/StudentContext';
import getGamificationProgress from '@/api/gamificationProgress/getGamificationProgress.endpoint';
import useSaveGamificationProgressMutation from '@/v2/domains/Student/Gamification/queries/useSaveGamificationProgressMutation.hook';

const GamificationContext = createContext({});

export function GamificationContextProvider({ children }) {
  const studentContext = useContext(StudentContext);
  const { mutateAsync: saveGamificationProgress } =
    useSaveGamificationProgressMutation();

  const { state, actions } = useCustomHook({
    defaultState: {
      loading: false,
      xp: null,
      stars: null,
      isGamificationStudent: false,
      gainedStars: 0,
      animatingStars: false,
    },
    actions: {
      loadData: async () => {
        try {
          actions.setState({ loading: true });
          const response = await getGamificationProgress();

          const xp = response.xp || 0;
          const stars = response.stars || 0;

          actions.setState({
            xp,
            stars,
            loading: false,
          });
        } catch (error) {
          actions.setState({ loading: false });
          openAPIErrorNotification({
            title: 'There was an error',
            description: 'There was a problem loading the your xp and stars',
            errorFeedback: error.feedbackData,
          });
          Sentry.captureException(error);
        }
      },
      putData: async ({
        activityId,
        miniactivityType,
        level,
        progress,
        correctAnswers,
      }) => {
        if (studentContext.state.teacherPreferences?.gamificationEnabled) {
          try {
            const prevStars = state.stars;
            const response = await saveGamificationProgress({
              activityId,
              data: {
                miniactivityType,
                level,
                progress,
                correctAnswers,
                saveId: uuidv4(),
              },
            });
            const { xp, stars } = response;

            actions.setState({
              xp,
              stars,
              gainedStars: stars - prevStars,
              animatingStars: true,
            });
          } catch (error) {
            Sentry.captureException(error);
            openAPIErrorNotification({
              title: 'There was an error',
              description: 'There was a problem saving the your xp and stars',
              errorFeedback: error.feedbackData,
            });
          }
        }
      },

      onStarsAnimationEnd: () => {
        actions.setState({ gainedStars: 0, animatingStars: false });
      },
    },
  });

  useEffect(() => {
    if (
      studentContext.state.teacherPreferences?.gamificationEnabled &&
      studentContext.state.currentClass &&
      !studentContext.state.isSharedStudent
    ) {
      actions.loadData();
    }
  }, [
    studentContext.state.teacherPreferences?.gamificationEnabled,
    studentContext.state.currentClass,
    studentContext.state.isSharedStudent,
  ]);

  return (
    <GamificationContext.Provider
      value={{
        state: {
          ...state,
          isGamificationStudent:
            studentContext.state.teacherPreferences?.gamificationEnabled &&
            studentContext.state.currentClass &&
            !studentContext.state.isSharedStudent,
        },
        actions,
      }}
    >
      {children}
    </GamificationContext.Provider>
  );
}

export const useGamificationContext = () => useContext(GamificationContext);
